class LapienaBundleBuilder {
  constructor(root) {
    this.root = root;

    this.requiredQuantity =
      Number(root.dataset.requiredQuantity) || 7;

    this.productCards = Array.from(
      root.querySelectorAll("[data-lapiena-product]")
    );

    this.countOutputs = Array.from(
      root.querySelectorAll("[data-lapiena-count]")
    );

    this.instructions = Array.from(
      root.querySelectorAll("[data-lapiena-instruction]")
    );

    this.selectionOutput =
      root.querySelector("[data-lapiena-selection]");

    this.addButton =
      root.querySelector("[data-lapiena-add]");

    this.clearButton =
      root.querySelector("[data-lapiena-clear]");

    this.message =
      root.querySelector("[data-lapiena-message]");

    this.quantities = new Map();

    this.productCards.forEach((card) => {
      this.quantities.set(card.dataset.variantId, 0);
    });

    this.bindEvents();
    this.updateInterface();
  }

  bindEvents() {
    this.productCards.forEach((card) => {
      const plusButton =
        card.querySelector("[data-lapiena-plus]");

      const minusButton =
        card.querySelector("[data-lapiena-minus]");

      plusButton?.addEventListener("click", () => {
        this.increaseProduct(card);
      });

      minusButton?.addEventListener("click", () => {
        this.decreaseProduct(card);
      });
    });

    this.clearButton?.addEventListener("click", () => {
      this.clearBundle();
    });

    this.addButton?.addEventListener("click", () => {
      this.addBundleToCart();
    });
  }

  getTotalQuantity() {
    return Array.from(this.quantities.values()).reduce(
      (total, quantity) => total + quantity,
      0
    );
  }

  getProductQuantity(card) {
    return (
      this.quantities.get(card.dataset.variantId) || 0
    );
  }

  getProductLimit(card) {
    const stockLimit = Number(card.dataset.stockLimit);

    if (Number.isFinite(stockLimit)) {
      return Math.min(
        stockLimit,
        this.requiredQuantity
      );
    }

    return this.requiredQuantity;
  }

  increaseProduct(card) {
    const totalQuantity = this.getTotalQuantity();

    const currentQuantity =
      this.getProductQuantity(card);

    const productLimit =
      this.getProductLimit(card);

    const isAvailable =
      card.dataset.available !== "false";

    if (!isAvailable) {
      this.showMessage(
        "This Lapiena filler is currently unavailable.",
        true
      );
      return;
    }

    if (totalQuantity >= this.requiredQuantity) {
      this.showMessage(
        `Your bundle can contain a maximum of ${this.requiredQuantity} boxes.`,
        true
      );
      return;
    }

    if (currentQuantity >= productLimit) {
      this.showMessage(
        "You have reached the available quantity for this product.",
        true
      );
      return;
    }

    this.quantities.set(
      card.dataset.variantId,
      currentQuantity + 1
    );

    this.showMessage("");
    this.updateInterface();
  }

  decreaseProduct(card) {
    const currentQuantity =
      this.getProductQuantity(card);

    if (currentQuantity <= 0) {
      return;
    }

    this.quantities.set(
      card.dataset.variantId,
      currentQuantity - 1
    );

    this.showMessage("");
    this.updateInterface();
  }

  clearBundle() {
    this.quantities.forEach((value, variantId) => {
      this.quantities.set(variantId, 0);
    });

    this.showMessage("");
    this.updateInterface();
  }

  updateInterface() {
    const totalQuantity =
      this.getTotalQuantity();

    const remainingQuantity =
      Math.max(
        this.requiredQuantity - totalQuantity,
        0
      );

    this.countOutputs.forEach((output) => {
      output.textContent =
        `${totalQuantity} / ${this.requiredQuantity}`;
    });

    this.instructions.forEach((instruction) => {
      if (totalQuantity === this.requiredQuantity) {
        instruction.textContent =
          "Your bundle is complete.";
      } else {
        instruction.textContent =
          `Choose ${remainingQuantity} more ${
            remainingQuantity === 1 ? "box" : "boxes"
          } to complete your bundle.`;
      }
    });

    this.productCards.forEach((card) => {
      const quantity =
        this.getProductQuantity(card);

      const productLimit =
        this.getProductLimit(card);

      const quantityOutput =
        card.querySelector(
          "[data-lapiena-quantity]"
        );

      const plusButton =
        card.querySelector(
          "[data-lapiena-plus]"
        );

      const minusButton =
        card.querySelector(
          "[data-lapiena-minus]"
        );

      const isAvailable =
        card.dataset.available !== "false";

      if (quantityOutput) {
        quantityOutput.textContent =
          String(quantity);
      }

      if (minusButton) {
        minusButton.disabled =
          quantity <= 0;
      }

      if (plusButton) {
        plusButton.disabled =
          !isAvailable ||
          totalQuantity >= this.requiredQuantity ||
          quantity >= productLimit;
      }

      card.classList.toggle(
        "is-selected",
        quantity > 0
      );
    });

    this.updateSelectionSummary();

    if (this.addButton) {
      const bundleComplete =
        totalQuantity === this.requiredQuantity;

      this.addButton.disabled =
        !bundleComplete;

      this.addButton.textContent =
        bundleComplete
          ? "Add bundle to bag"
          : `Choose ${this.requiredQuantity} boxes`;
    }

    if (this.clearButton) {
      this.clearButton.disabled =
        totalQuantity === 0;
    }
  }

  updateSelectionSummary() {
    if (!this.selectionOutput) {
      return;
    }

    const selectedProducts =
      this.productCards
        .map((card) => {
          const quantity =
            this.getProductQuantity(card);

          if (quantity <= 0) {
            return null;
          }

          return {
            title: card.dataset.productTitle,
            quantity
          };
        })
        .filter(Boolean);

    if (selectedProducts.length === 0) {
      this.selectionOutput.innerHTML = `
        <p>
          Your selected Lapiena fillers will appear here.
        </p>
      `;
      return;
    }

    this.selectionOutput.innerHTML =
      selectedProducts
        .map(
          (product) => `
            <div>
              <span>${this.escapeHtml(product.title)}</span>
              <strong>× ${product.quantity}</strong>
            </div>
          `
        )
        .join("");
  }

  escapeHtml(value) {
    const element =
      document.createElement("div");

    element.textContent =
      String(value);

    return element.innerHTML;
  }

  showMessage(text, isError = false) {
    if (!this.message) {
      return;
    }

    this.message.textContent = text;

    this.message.classList.toggle(
      "is-error",
      isError
    );
  }

  getCartItems() {
    return this.productCards
      .map((card) => {
        const quantity =
          this.getProductQuantity(card);

        if (quantity <= 0) {
          return null;
        }

        return {
          id: Number(card.dataset.variantId),
          quantity,
          properties: {
            "_Custom bundle":
              "Lapiena Buy 5 Get 2 Free"
          }
        };
      })
      .filter(Boolean);
  }

  async addBundleToCart() {
    const totalQuantity =
      this.getTotalQuantity();

    if (
      totalQuantity !==
      this.requiredQuantity
    ) {
      this.showMessage(
        `Please choose exactly ${this.requiredQuantity} boxes.`,
        true
      );
      return;
    }

    const items =
      this.getCartItems();

    if (!items.length) {
      return;
    }

    this.addButton.disabled = true;
    this.addButton.textContent =
      "Adding your bundle…";

    this.showMessage("");

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({
            items
          })
        }
      );

      const responseData =
        await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.description ||
          responseData.message ||
          "Your bundle could not be added."
        );
      }

      this.addButton.textContent =
        "Added to your bag";

      this.showMessage(
        "Your Lapiena bundle has been added."
      );

       document.dispatchEvent(
  new CustomEvent("cart-drawer:refresh", {
    bubbles: true
  })
);

      window.setTimeout(() => {
        this.addButton.disabled = false;
        this.addButton.textContent =
          "Add bundle to bag";
      }, 1500);

    } catch (error) {
      console.error(
        "Lapiena bundle error:",
        error
      );

      this.showMessage(
        error.message ||
          "Something went wrong. Please refresh and try again.",
        true
      );

      this.addButton.disabled = false;
      this.addButton.textContent =
        "Add bundle to bag";
    }
  }
}


function initialiseLapienaBundleBuilders(
  container = document
) {
  const builders =
    container.querySelectorAll(
      "[data-lapiena-builder]"
    );

  builders.forEach((builder) => {
    if (
      builder.dataset.lapienaInitialised ===
      "true"
    ) {
      return;
    }

    builder.dataset.lapienaInitialised =
      "true";

    new LapienaBundleBuilder(builder);
  });
}


document.addEventListener(
  "DOMContentLoaded",
  () => {
    initialiseLapienaBundleBuilders();
  }
);


document.addEventListener(
  "shopify:section:load",
  (event) => {
    initialiseLapienaBundleBuilders(
      event.target
    );
  }
);


initialiseLapienaBundleBuilders();